import {put, call, all} from 'redux-saga/effects';
import Invoice from "../../entities/invoice";
import * as Actions from "../actions/invoiceActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';
import FinancialHelper from '../../Financial/FinancialHelper';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type InvoiceAction = {
    invoiceId: string;
    invoice: Invoice;
    invoices: Invoice[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneInvoice(action: InvoiceAction) {
	const session = tabSession.captureGeneration();
	try {
		const invoice: Invoice = yield call(api.findOne, 'invoice', action.invoiceId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_FETCH_ONE_SUCCESS, invoiceId: action.invoiceId, invoice, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICE_FETCH_ONE_FAILURE, invoiceId: action.invoiceId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {InvoiceAction} action
 */
export function* saveInvoice(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ INVOICE_SAVE saga', action.invoice?.id);

		if (action.invoice === undefined || action.invoice === null) {
			console.error('saveInvoice received a null');
			throw new Error('saveInvoice received a null');
		}

		if (!(action.invoice instanceof Invoice)) {
			action.invoice = new Invoice(action.invoice);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.invoice.addedOn;

		try {
		// OWA-860: queue the same current totals shown by the draft invoice.
		action.invoice = FinancialHelper.withCurrentDraftTotals(action.invoice);

			// Save to IndexedDB
			ensureOperationId(action.invoice);
			const addResult = yield call(idb.add, 'invoice', action.invoice);
			if (addResult.isErr()) {
				console.error(`⮂ saveInvoice: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertInvoiceSave(action.invoice, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.INVOICE_SAVE_FAILURE, invoice: action.invoice, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.INVOICE_SAVE, invoice: action.invoice, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertInvoiceSave(action.invoice, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.INVOICE_SAVE_FAILURE, invoice: action.invoice, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {InvoiceAction} action
 * @param {Invoice[]} action.invoices
 */
export function* saveManyInvoices(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ INVOICE_SAVE_MANY saga', action.invoices.map(invoice => invoice.id).join(','));

        if (!Array.isArray(action.invoices)) {
            console.error('saveManyInvoices expected an array, received', action.invoices);
            throw new Error('saveManyInvoices expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveInvoice), under these queue keys
		const dispatchedAddedOns: number[] = action.invoices.map(invoice => invoice?.addedOn);
		const revertAll = () => action.invoices.map((invoice, i) =>
			put(Actions.revertInvoiceSave(invoice, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Invoice[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.invoices.length; i++) {
				const invoice = FinancialHelper.withCurrentDraftTotals(action.invoices[i]);
				invoice.persisted = false;
				ensureOperationId(invoice);
				toSave.push(invoice);
			}

			const addResult = yield call(idb.add, 'invoice', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyInvoices: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.INVOICE_SAVE_MANY_FAILURE, invoices: action.invoices, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.INVOICE_SAVE_MANY, invoices: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.INVOICE_SAVE_MANY_FAILURE, invoices: action.invoices, status: 500, err});

			console.error(`Error saving ${action.invoices.length} invoices to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {InvoiceAction} action
 */
export function* removeInvoice(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.invoice === undefined || action.invoice === null || !(action.invoice instanceof Invoice)) {
			console.error('removeInvoice expected type Invoice, received', action.invoice);
			reportQueued(false);
			throw new Error('removeInvoice expected type Invoice');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.invoice.status = 'D';
		action.invoice.addedOn = newHRTimestamp();
		action.invoice.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.invoice);
		const addResult = yield call(idb.add, 'invoice', action.invoice);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeInvoice: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.INVOICE_REMOVE_FAILURE, invoice: action.invoice, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_REMOVE, invoice: action.invoice});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICE_REMOVE_FAILURE, invoice: action.invoice, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Invoice.
	*
	* @param {InvoiceAction} action
	*/
export function* restoreInvoice(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreInvoice expected getCurrentUserId() to return a value');
        return;
    }

	if (action.invoice === undefined || action.invoice === null) {
        console.error('restoreInvoice expected a Invoice, received null');
        throw new Error('restoreInvoice expected a Invoice, received null');
    }
    else if (!(action.invoice instanceof Invoice)) action.invoice = new Invoice(action.invoice); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.invoice.status = 'U';
		action.invoice.addedOn = newHRTimestamp();
		action.invoice.addedBy = userId;
		action.invoice.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.invoice);
		const addResult = yield call(idb.add, 'invoice', action.invoice);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreInvoice: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICE_RESTORE_FAILURE, invoice: action.invoice, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_RESTORE, invoice: action.invoice});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICE_RESTORE_FAILURE, invoice: action.invoice, status: 500, err});

		console.error('Error restoring from database', err);
	}
}